import {
  FilterField,
  FilterVocabulary
} from '@core/filters/path-filter/vocabulary/vocabulary';
import { fieldPath } from '@core/filters/path-filter/wire';
import { catalogueEntry } from '@core/models/recipe-parameter-catalogue';
import {
  RecipeParameterFieldErrorCode,
  RecipeParameterFilterContext,
  RecipeParameterKind,
  RecipeParameterProperty,
  RecipeParameterSchema,
  RecipeParameterValue,
  RecipeParameterValues
} from '@core/models/recipe-parameters.model';

/** A recipe with no placeholders yields an empty `properties` map: no form is needed. */
export function hasParameters(schema: RecipeParameterSchema | null | undefined): boolean {
  return Object.keys(schema?.properties ?? {}).length > 0;
}

/**
 * The parameter names in the order the recipe author intended.
 *
 * `properties` key order is alphabetical (the document is serialized from a
 * sorted map), so `x-parameter-order` is the only source of authored order. Any
 * property the order omits is appended rather than dropped — a required field
 * that never rendered could never be filled, making the form unsubmittable.
 */
export function orderedParameterNames(
  schema: RecipeParameterSchema | null | undefined
): string[] {
  const properties = schema?.properties ?? {};
  const known = new Set(Object.keys(properties));
  const ordered = (schema?.['x-parameter-order'] ?? []).filter((name) => known.has(name));
  const seen = new Set(ordered);
  return [...ordered, ...Object.keys(properties).filter((name) => !seen.has(name))];
}

/**
 * The widget a property renders as. An unrecognised `type`/`format` pair falls
 * back to a text input rather than failing, because the data layer may add
 * formats GFE has not seen.
 */
export function widgetFor(
  property: RecipeParameterProperty | undefined
): RecipeParameterKind {
  if (property?.type === 'array') return 'list';
  if (property?.format === 'date') return 'date';
  if (property?.format === 'date-time') return 'date-time';
  return 'text';
}

/** Every parameter is required in v1: there are no optional parameters and no defaults. */
export function isRequired(
  schema: RecipeParameterSchema | null | undefined,
  name: string
): boolean {
  const required = schema?.required;
  return required === undefined ? true : required.includes(name);
}

export function minItemsFor(property: RecipeParameterProperty | undefined): number {
  return property?.minItems ?? 1;
}

export function emptyValueFor(widget: RecipeParameterKind): RecipeParameterValue {
  return widget === 'list' ? [] : '';
}

/** A blank value per property, so every field is present and controlled from the start. */
export function initialValues(
  schema: RecipeParameterSchema | null | undefined
): RecipeParameterValues {
  const properties = schema?.properties ?? {};
  const values: RecipeParameterValues = {};
  for (const name of orderedParameterNames(schema)) {
    values[name] = emptyValueFor(widgetFor(properties[name]));
  }
  return values;
}

/**
 * The caption for a parameter: the catalogue's localized label, falling back to
 * the schema's `title`, then the raw name.
 *
 * `title` is always present and always English-ish — the data layer humanizes
 * the parameter name (`target_date` → "Target date"). It is a floor for
 * debugging and for consumers without a catalogue, not a caption to localize.
 *
 * This is deliberately not the `x-i18n-title` mechanism used for entity
 * schemas: a recipe parameter name is admin-authored data, so it carries no
 * `x-i18n-*` annotations.
 */
export function resolveParameterLabel(
  name: string,
  property: RecipeParameterProperty | undefined,
  translate: (key: string) => string
): string {
  const entry = catalogueEntry(name);
  if (entry !== undefined) {
    const label = translate(entry.labelKey);
    if (label.length > 0 && label !== entry.labelKey) return label;
  }
  return property?.title ?? name;
}

/** Help text from the catalogue, or `undefined` when there is none to show. */
export function resolveParameterHelp(
  name: string,
  translate: (key: string) => string
): string | undefined {
  const entry = catalogueEntry(name);
  if (entry === undefined) return undefined;
  const help = translate(entry.descriptionKey);
  return help.length > 0 && help !== entry.descriptionKey ? help : undefined;
}

/**
 * Validates collected values against the schema. The data layer is
 * authoritative; this exists to block an obviously invalid submission and to
 * annotate the offending fields.
 */
export function validateValues(
  schema: RecipeParameterSchema | null | undefined,
  values: RecipeParameterValues
): Record<string, RecipeParameterFieldErrorCode> {
  const properties = schema?.properties ?? {};
  const errors: Record<string, RecipeParameterFieldErrorCode> = {};

  for (const name of orderedParameterNames(schema)) {
    const property = properties[name];
    const value = values[name];
    const required = isRequired(schema, name);

    if (widgetFor(property) === 'list') {
      if (!Array.isArray(value)) {
        errors[name] = value === undefined ? 'required' : 'type';
        continue;
      }
      if (value.some((item) => typeof item !== 'string')) {
        errors[name] = 'type';
        continue;
      }
      const filled = value.filter((item) => item.trim().length > 0);
      if (filled.length === 0) {
        if (required) errors[name] = 'required';
        continue;
      }
      if (filled.length < minItemsFor(property)) errors[name] = 'min-items';
      continue;
    }

    if (value === undefined) {
      if (required) errors[name] = 'required';
      continue;
    }
    if (typeof value !== 'string') {
      errors[name] = 'type';
      continue;
    }
    if (value.trim().length === 0 && required) errors[name] = 'required';
  }

  return errors;
}

export function isValid(
  schema: RecipeParameterSchema | null | undefined,
  values: RecipeParameterValues
): boolean {
  return Object.keys(validateValues(schema, values)).length === 0;
}

/**
 * The payload to submit: exactly the schema's properties, trimmed, with empty
 * array entries dropped.
 *
 * The schema sets `additionalProperties: false`, so any key the schema does not
 * declare would be rejected. Building the payload from the schema rather than
 * from the form's state makes it impossible for UI bookkeeping to leak in.
 */
export function toSubmissionPayload(
  schema: RecipeParameterSchema | null | undefined,
  values: RecipeParameterValues
): RecipeParameterValues {
  const properties = schema?.properties ?? {};
  const payload: RecipeParameterValues = {};

  for (const name of orderedParameterNames(schema)) {
    const value = values[name];
    if (widgetFor(properties[name]) === 'list') {
      payload[name] = (Array.isArray(value) ? value : [])
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      continue;
    }
    payload[name] = typeof value === 'string' ? value.trim() : '';
  }

  return payload;
}

/** `target_date` → `Target date`, matching how the data layer derives a `title`. */
export function humanizeParameterName(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced.length === 0 ? name : spaced[0].toUpperCase() + spaced.slice(1);
}

export interface DescribedRecipeParameter {
  name: string;
  label: string;
  value: string;
}

/**
 * Labels a plan's stored parameter values for display. There is no schema on a
 * plan read, so keys with no catalogue entry are humanized locally.
 */
export function describeParameterValues(
  values: RecipeParameterValues | null | undefined,
  translate: (key: string) => string
): DescribedRecipeParameter[] {
  return Object.entries(values ?? {}).map(([name, value]) => {
    const entry = catalogueEntry(name);
    const label = entry !== undefined ? translate(entry.labelKey) : undefined;
    return {
      name,
      label:
        label !== undefined && label.length > 0 && label !== entry?.labelKey
          ? label
          : humanizeParameterName(name),
      value: Array.isArray(value) ? value.join(', ') : String(value ?? '')
    };
  });
}

/**
 * Renders an ISO 8601 instant for a `datetime-local` input, which has no
 * timezone and so must be fed local wall-clock time.
 */
export function isoToDatetimeLocal(value: string | undefined | null): string {
  if (value === undefined || value === null || value.length === 0) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const seconds = date.getSeconds() === 0 ? '' : `:${pad(date.getSeconds())}`;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}${seconds}`
  );
}

/** Converts a `datetime-local` value to the ISO 8601 instant the contract expects. */
export function datetimeLocalToIso(value: string | undefined | null): string {
  if (value === undefined || value === null || value.length === 0) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

export interface SchemaValidationErrorMapping {
  /** Parameter name → the data layer's message for it. */
  fields: Record<string, string>;
  /** Messages that could not be attributed to a field, so nothing is swallowed. */
  unmatched: string[];
}

const INSTANCE_PATH_KEYS = ['instancePath', 'instance_path', 'path', 'field', 'property'];
const MESSAGE_KEYS = ['message', 'explanation', 'detail', 'description', 'reason'];

/** `#/target_date`, `/target_date`, `target_date/0` → `target_date`. */
function firstPathSegment(raw: string): string | undefined {
  const cleaned = raw.replace(/^#/, '').replace(/^\//, '');
  const segment = cleaned.split('/')[0]?.trim();
  return segment !== undefined && segment.length > 0 ? segment : undefined;
}

function pickString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
}

/**
 * Maps a `schema_validation_error`'s details onto form fields.
 *
 * The details shape is not pinned by the contract beyond "the details list the
 * offending instance paths", so this walks the payload permissively and reports
 * anything it cannot attribute instead of dropping it.
 */
export function mapSchemaValidationErrors(
  schema: RecipeParameterSchema | null | undefined,
  details: unknown
): SchemaValidationErrorMapping {
  const known = new Set(Object.keys(schema?.properties ?? {}));
  const fields: Record<string, string> = {};
  const unmatched: string[] = [];

  const attribute = (rawPath: string | undefined, message: string | undefined): void => {
    const name = rawPath !== undefined ? firstPathSegment(rawPath) : undefined;
    if (name !== undefined && known.has(name)) {
      fields[name] ??= message ?? '';
      return;
    }
    const fallback = message ?? rawPath;
    if (fallback !== undefined && !unmatched.includes(fallback)) unmatched.push(fallback);
  };

  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node === 'string') {
      const match = /(?:^|\s)#?\/([A-Za-z0-9_]+)/.exec(node);
      attribute(match?.[1], node);
      return;
    }
    if (typeof node !== 'object' || node === null) return;

    const record = node as Record<string, unknown>;
    const path = pickString(record, INSTANCE_PATH_KEYS);
    const message = pickString(record, MESSAGE_KEYS);
    if (path !== undefined || message !== undefined) {
      attribute(path, message);
    }

    for (const [key, value] of Object.entries(record)) {
      if (INSTANCE_PATH_KEYS.includes(key) || MESSAGE_KEYS.includes(key)) continue;
      walk(value);
    }
  };

  walk(details);
  return { fields, unmatched };
}

/**
 * Splits a path-filter context field `edge.leaf`. Root leaves may contain a dot themselves
 * (`#assoc:kpi.assigned_jobs`, `metadata.<key>`), so only a plain identifier other than
 * `metadata` before the first dot is read as an edge.
 */
export function pathFilterHop(
  field: string | undefined
): { edge: string; leaf: string } | null {
  if (!field) return null;
  const dot = field.indexOf('.');
  if (dot <= 0) return null;
  const edge = field.slice(0, dot);
  if (edge === 'metadata' || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(edge)) return null;
  return { edge, leaf: field.slice(dot + 1) };
}

export interface SlotPicker {
  uidEntityType: string | null;
  closed: Pick<FilterField, 'enumValues' | 'enumLabels'> | null;
}

/**
 * The picker a parameter's value takes: the entity a slot's field refers to, or the closed values
 * it serves, from the first slot that has either. `null` leaves the input its kind takes.
 */
export function slotPickerFor(
  contexts: readonly RecipeParameterFilterContext[],
  vocabularyOf: (entityType: string) => FilterVocabulary | null
): SlotPicker | null {
  for (const { entityType, field: name, grammar } of contexts) {
    if (!entityType || !name) continue;
    const hop = grammar === 'pathFilter' ? pathFilterHop(name) : null;
    const field = vocabularyOf(entityType)?.field(
      hop?.edge ?? null,
      fieldPath(hop?.leaf ?? name)
    );
    if (field?.uidEntityType) return { uidEntityType: field.uidEntityType, closed: null };
    if (field?.enumValues?.length) {
      return {
        uidEntityType: null,
        closed: { enumValues: field.enumValues, enumLabels: field.enumLabels }
      };
    }
  }
  return null;
}
